<template>
  <v-container fluid py-1>
    <v-layout row wrap>
      <v-expansion-panel v-model="panel" expand>
        <v-toolbar card flat dense color="white">
          <v-toolbar-title>{{ $tc('models.phase', 2) }}</v-toolbar-title>
          <v-spacer />
          <meta-button type="new" @click="dialog = true" />
        </v-toolbar>
        <v-expansion-panel-content
          v-for="(phase, index) in localPhases"
          :key="index + 20"
        >
          <template #header>
            <div class="title">
              {{ getName(phase.name) }}
            </div>
          </template>
          <v-card>
            <v-card-text>
              <div class="v-table__overflow">
                <table class="text-xs-center v-datatable v-table theme--light">
                  <thead>
                    <tr>
                      <th>{{ $t('fields.global.order') }}</th>
                      <th>{{ $t('fields.global.state') }}</th>
                      <th>{{ $t('fields.global.name') }}</th>
                      <th>{{ $t('titles.tournaments.ifShowResults') }}</th>
                      <th>{{ $t('titles.tournaments.ifRandomMatches') }}</th>
                      <th>{{ $t('fields.phases.typePlayOff') }}</th>
                      <th>{{ $t('fields.phases.toClass') }}</th>
                      <th>{{ $t('fields.phases.matchesByParticipant') }}</th>
                      <th>{{ $t('fields.tournaments.hours') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="py-2">
                        {{ phase.order }}
                      </td>
                      <td>
                        <button-state :state="phase.active" />
                      </td>
                      <td>{{ getName(phase.name) }}</td>
                      <td>
                        <button-state :state="phase.showResults" />
                      </td>
                      <td>
                        <button-state :state="phase.randomMatches" />
                      </td>
                      <td>
                        {{
                          phase.typePlayOff
                            ? $t(`titles.phases.${phase.typePlayOff}`)
                            : ''
                        }}
                      </td>
                      <td>
                        {{
                          phase.typePlayOff === 'ranking' ? phase.toClass : '-'
                        }}
                      </td>
                      <td>
                        {{
                          phase.typePlayOff === 'ranking'
                            ? phase.matchesByParticipant
                            : '-'
                        }}
                      </td>
                      <td>
                        <template v-for="(hour, index) in phase.hours">
                          <v-chip
                            :key="index + hour"
                            small
                            color="primary"
                            dark
                          >
                            {{ hour }}
                          </v-chip>
                        </template>
                      </td>
                      <hr />
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>
          </v-card>
          <v-divider />
          <v-card>
            <v-card-text>
              <div class="title text-xs-center">
                {{
                  phase.groups.length
                    ? $tc('models.group', 2)
                    : $t('titles.groups.noGroup')
                }}
              </div>
              <div v-if="phase.groups.length" class="v-table__overflow">
                <table class="v-datatable v-table theme--light">
                  <thead>
                    <tr>
                      <th>{{ $t('fields.global.actions') }}</th>
                      <th>{{ $t('fields.global.order') }}</th>
                      <th>{{ $t('fields.global.state') }}</th>
                      <th>{{ $t('fields.global.name') }}</th>
                      <th>{{ $tc('models.pitch', 2) }}</th>
                      <th>{{ $tc('models.match', 2) }}</th>
                    </tr>
                  </thead>
                  <draggable
                    v-model="phase.groups"
                    :handle="handler(phase.groups)"
                    tag="tbody"
                    :class="{ draggable: handler(phase.groups) !== 'none' }"
                    @end="dragEnd(phase)"
                  >
                    <tr
                      v-for="(group, index) in phase.groups"
                      :key="index + 20"
                    >
                      <td class="handle">
                        <v-icon
                          color="success"
                          class="mr-1"
                          @click="setOne(phase._id, group._id)"
                        >
                          edit
                        </v-icon>
                        <v-icon
                          color="error"
                          @click="ifDelete(phase._id, group._id)"
                        >
                          delete
                        </v-icon>
                      </td>
                      <td class="py-2">
                        {{ group.order }}
                      </td>
                      <td>
                        <button-state :state="group.active" />
                      </td>
                      <td>{{ getName(group.name) }}</td>
                      <td>
                        <template v-if="Array.isArray(group.pitch)" />
                        <v-chip
                          v-for="pitch of group.pitch"
                          :key="pitch._id"
                          color="transparent"
                        >
                          <v-avatar v-if="pitch.photo" size="32">
                            <img :src="pitch.photo" />
                          </v-avatar>
                          <v-list-tile-content>
                            <v-list-tile-title>
                              {{ pitch.name }}
                            </v-list-tile-title>
                            <v-list-tile-sub-title
                              class="caption"
                              style="color: rgba(0, 0, 0, 0.54)"
                            >
                              {{ pitch.stadium }}
                            </v-list-tile-sub-title>
                          </v-list-tile-content>
                        </v-chip>
                      </td>
                      <td>
                        <template v-for="(match, index) in group.matches">
                          <v-chip :key="index + 220" color="transparent">
                            <v-chip color="transparent" class="pr-2">
                              {{ formatDate(match.date) }}
                              -
                              {{ match.time }}
                            </v-chip>
                            <v-chip color="transparent">
                              <v-avatar size="32">
                                <img :src="match.homeTeamLogo" />
                              </v-avatar>
                              <v-list-tile-content>
                                <v-list-tile-title>
                                  <span>{{ match.homeTeam }}</span>
                                </v-list-tile-title>

                                <v-list-tile-sub-title
                                  v-if="Array.isArray(match.homeTeamCategory)"
                                  class="caption"
                                  style="color: rgba(0, 0, 0, 0.54)"
                                >
                                  {{ match.homeTeamCategory.join(' - ') }}
                                </v-list-tile-sub-title>
                              </v-list-tile-content>
                            </v-chip>
                            <span class="px-2">vs</span>
                            <v-chip color="transparent">
                              <v-avatar size="32">
                                <img :src="match.visitorTeamLogo" />
                              </v-avatar>
                              <v-list-tile-content>
                                <v-list-tile-title>
                                  <span>{{ match.visitorTeam }}</span>
                                </v-list-tile-title>

                                <v-list-tile-sub-title
                                  v-if="
                                    Array.isArray(match.visitorTeamCategory)
                                  "
                                  class="caption"
                                  style="color: rgba(0, 0, 0, 0.54)"
                                >
                                  {{ match.visitorTeamCategory.join(' - ') }}
                                </v-list-tile-sub-title>
                              </v-list-tile-content>
                            </v-chip>
                          </v-chip>
                        </template>
                      </td>

                      <hr />
                    </tr>
                  </draggable>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-layout>
    <dialog-delete
      :dialog.sync="dialogDelete"
      :loading="loading"
      @delete="_setDelete(idPhaseDelete, idDelete)"
    />
    <dialog-group
      ref="form"
      :dialog.sync="dialog"
      :group.sync="group"
      :phases="phases"
      :type="type"
      :loading="loading"
      @submit="submit"
    />
  </v-container>
</template>

<script>
import draggable from 'vuedraggable'
import { mapActions, mapState } from 'vuex'
// import { dateFormat, timeFormat } from '../../../helpers/dateHelper'
import DialogDelete from '@/components/widgets/dialog/DialogDelete'
import DialogGroup from '@/components/widgets/dialog/DialogGroup'
import { formatDate } from '@/helpers'
import { parseObject } from '@/helpers/utils'
export default {
  name: 'TabGroups',
  components: { draggable, DialogDelete, DialogGroup },
  props: {
    phases: {
      type: Array,
      required: true,
      default: () => [],
    },
    type: {
      type: String,
      required: true,
      default: '',
    },
  },
  data() {
    return {
      group: {},
      phase: {},
      localPhases: [],
      dialog: false,
      dialogDelete: false,
      loadingDelete: false,
      panel: [],
      idDelete: '',
      idPhaseDelete: '',
    }
  },
  watch: {
    selected: {
      handler: function (val) {
        this.group = parseObject(val)
        this.group.name = this.selected.name
      },
      immediate: true,
      deep: true,
    },
    tournament: {
      handler: function (val) {
        this.localPhases = Array.from(val.phases || [])
        this.clear()
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.group = parseObject(this.selected)
    this.group.name = this.selected.name
    this.clear()
  },
  computed: {
    ...mapState({
      selected: (state) => state.groups.selected,
      loading: (state) => state.groups.loading,
      loadingTour: (state) => state.tournaments.loading,
      loadingPhase: (state) => state.phases.loading,
      tournamentId: (state) => state.tournaments.idSelected,
      tournament: (state) => state.tournaments.selected,
    }),
  },
  methods: {
    ...mapActions({
      fetchOne: 'groups/fetchOne',
      setCreate: 'groups/create',
      setUpdate: 'groups/update',
      setDelete: 'groups/delete',
      setUpdatePhase: 'phases/update',
    }),
    handler(groups) {
      if (
        this.loading ||
        this.loadingTour ||
        this.loadingPhase ||
        !Array.isArray(groups) ||
        groups.length <= 1 ||
        !this.can('edit', 'tournament', this.tournament)
      )
        return 'none'
      else if (this.isMobile) return '.handle'
      else return ''
    },
    submit() {
      let payload = Object.assign({}, this.group)
      if (!payload._id) {
        let maxOrder = 0
        let phase = this.phases.find((phase) => phase._id === payload.phaseId)
        phase.groups.forEach((group) => {
          if (group.order > maxOrder) maxOrder = group.order
        })
        payload.order = maxOrder + 1
        this.setCreate(payload).then((_) => {
          this.$refs.form.cancel()
        })
      } else {
        this.setUpdate(payload).then((_) => {
          this.$refs.form.cancel()
        })
      }
    },
    setOne(phaseId, groupId) {
      this.fetchOne({ phaseId, groupId }).then((_) => {
        this.$nextTick((_) => {
          this.dialog = true
        })
      })
    },
    _setDelete(phaseId, groupId) {
      this.setDelete({ phaseId, groupId }).then((_) => {
        this.dialogDelete = false
      })
    },

    ifDelete(phaseId, groupId) {
      this.idPhaseDelete = phaseId
      this.idDelete = groupId
      this.dialogDelete = true
    },
    dragEnd(phase) {
      if (!this.can('edit', 'tournament', this.tournament)) return

      const listGroups = phase.groups.map((group, index) => ({
        _id: group._id,
        order: index + 1,
      }))
      let payload = {
        _id: phase._id,
        groups: listGroups,
      }
      this.setUpdatePhase(payload)
    },

    clear() {
      this.panel = Array.from({ length: this.phases.length }, (v, i) => true)
    },
    formatDate(date) {
      return formatDate(date)
    },
  },
}
</script>
